import { ChangeDetectionStrategy, Component, input, InputSignal, Signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';

const REQUIRED_MESSAGE: string = 'Dieses Feld muss gesetzt sein.';

interface MaxLengthError {
  requiredLength: number;
  actualLength: number;
}

/**
 * Zeigt die Fehlertexte eines Controls an, sobald es berührt wurde. Angezeigt werden Fehler, deren Wert ein Text ist
 * (siehe bewerber-validators.ts), sowie `required` und `maxlength`. Andere Fehler bleiben unberücksichtigt.
 */
@Component({
  selector: 'app-field-error',
  templateUrl: './field-error.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FieldErrorComponent {
  public readonly control: InputSignal<AbstractControl> = input.required<AbstractControl>();

  // control.events meldet auch markAllAsTouched(), das weder valueChanges noch statusChanges auslöst.
  protected readonly messages: Signal<string[]> = toSignal(
    toObservable(this.control).pipe(
      switchMap((control: AbstractControl): Observable<string[]> =>
        control.events.pipe(
          startWith(null),
          map((): string[] => (control.invalid && control.touched ? this.toMessages(control.errors) : []))
        )
      )
    ),
    { initialValue: [] }
  );

  private toMessages(errors: ValidationErrors | null): string[] {
    if (errors === null) {
      return [];
    }
    return Object.entries(errors)
      .map(([key, value]: [string, unknown]): string | null => {
        if (typeof value === 'string') {
          return value;
        }
        if (key === 'required') {
          return REQUIRED_MESSAGE;
        }
        if (key === 'maxlength') {
          return `Es sind maximal ${(value as MaxLengthError).requiredLength} Zeichen erlaubt.`;
        }
        return null;
      })
      .filter((message: string | null): message is string => message !== null);
  }
}
